import { motion } from 'framer-motion';

export default function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white bg-opacity-90 rounded-2xl shadow-xl p-8"
      >
        <div className="mb-6">
          <h1 className="text-3xl font-display font-bold text-center text-tomato-700 mb-2">About the Pomodoro Technique</h1>
          <div className="w-20 h-1 bg-tomato-600 mx-auto my-4"></div>
        </div>
        
        <div className="prose prose-tomato lg:prose-lg max-w-none">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-8">
            <div className="w-40 h-40 md:w-48 md:h-48 flex-shrink-0 flex items-center justify-center">
              <div 
                className="w-full h-full" 
                style={{
                  backgroundImage: 'url("/images/tomato.png")',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              ></div>
            </div>
            
            <div>
              <h2 className="text-2xl font-display font-bold text-tomato-600 mb-4">What is the Pomodoro Technique?</h2>
              <p>
                The Pomodoro Technique is a time management method developed by Francesco Cirillo in the late 1980s. 
                The technique uses a timer to break down work into intervals, traditionally 25 minutes in length, 
                followed by a 5-minute break. Each complete unit (25 minutes of focused work plus the 5-minute break) is known as a "pomodoro", 
                from the Italian word for tomato, after the tomato-shaped kitchen timer that Cirillo used as a university student.
              </p>
            </div>
          </div>
          
          <h2 className="text-2xl font-display font-bold text-tomato-600 mb-4">How it Works</h2>
          
          <div className="bg-tomato-50 p-6 rounded-xl border border-tomato-100 mb-8 shadow-md">
            <ol className="space-y-6">
              <li className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-tomato-600 rounded-full flex items-center justify-center text-white font-bold mr-4 shadow-sm">1</div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-tomato-700">Plan Your Tasks</h3>
                  <p>Decide what you want to accomplish and estimate how many pomodoros (usually 25-minute focus sessions) you'll need for each task.</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-tomato-600 rounded-full flex items-center justify-center text-white font-bold mr-4 shadow-sm">2</div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-tomato-700">Do One Pomodoro</h3>
                  <p>Set a timer for 25 minutes. Focus entirely on your task until the timer rings, then take a 5-minute break. Avoid multitasking or distractions during this time. Together, the 25-minute focus period plus the 5-minute break constitute one complete pomodoro.</p>
                </div>
              </li>
              
              <li className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-tomato-600 rounded-full flex items-center justify-center text-white font-bold mr-4 shadow-sm">3</div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-tomato-700">Complete Four Pomodori</h3>
                  <p>After completing four pomodori (the Italian plural of pomodoro), take a longer break of 10-45 minutes to recharge.</p>
                </div>
              </li>
            </ol>
          </div>
          
          <h2 className="text-2xl font-display font-bold text-tomato-600 mb-4">Benefits of the Pomodoro Technique</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-tomato-50 p-5 rounded-xl shadow-md transform hover:scale-105 transition-transform">
              <h3 className="font-bold text-lg mb-2 text-tomato-700">Improved Focus</h3>
              <p>Short, timed sessions make it easier to stay on task and resist distractions.</p>
            </div>
            
            <div className="bg-tomato-50 p-5 rounded-xl shadow-md transform hover:scale-105 transition-transform">
              <h3 className="font-bold text-lg mb-2 text-tomato-700">Better Time Management</h3>
              <p>By breaking your day into pomodoros, you can plan and track progress easily.</p>
            </div>
            
            <div className="bg-tomato-50 p-5 rounded-xl shadow-md transform hover:scale-105 transition-transform">
              <h3 className="font-bold text-lg mb-2 text-tomato-700">Reduced Mental Fatigue</h3>
              <p>Regular breaks help prevent burnout and keep your mind fresh.</p>
            </div>
            
            <div className="bg-tomato-50 p-5 rounded-xl shadow-md transform hover:scale-105 transition-transform">
              <h3 className="font-bold text-lg mb-2 text-tomato-700">Increased Accountability</h3>
              <p>Tracking completed pomodoros provides a concrete measure of effort.</p>
            </div>
          </div>
          
          <h2 className="text-2xl font-display font-bold text-tomato-600 mb-4">Tips for Success</h2>
          
          <ul className="space-y-3 mb-8 bg-white bg-opacity-60 p-6 rounded-xl">
            <li className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-tomato-600 rounded-full mt-1 mr-3"></div>
              <p><span className="font-bold text-tomato-700">Protect your pomodoro from distractions</span> — make sure you spend the full 25 minutes on your task. If people want things, tell them you'll get back to them after your pomodoro.</p>
            </li>
            
            <li className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-tomato-600 rounded-full mt-1 mr-3"></div>
              <p><span className="font-bold text-tomato-700">Properly break in your breaks</span> — no sneaky working. Step away, move around, or grab a snack.</p>
            </li>
            
            <li className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-tomato-600 rounded-full mt-1 mr-3"></div>
              <p><span className="font-bold text-tomato-700">It's flexible, not rigid</span> — If you need longer or shorter times, that's OK. It's about what helps you focus.</p>
            </li>
            
            <li className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-tomato-600 rounded-full mt-1 mr-3"></div>
              <p><span className="font-bold text-tomato-700">Log your distractions</span> — When a distraction pops up, write it down and get back to it later, rather than acting on it immediately.</p>
            </li>
          </ul>
          
          <h2 className="text-2xl font-display font-bold text-tomato-600 mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-6 mb-8 bg-tomato-50 p-6 rounded-xl shadow-md">
            <div>
              <h3 className="font-bold text-lg mb-1 text-tomato-700">Can I adjust the timing?</h3>
              <p>Yes! While the traditional method uses 25-minute work periods with 5-minute breaks, you can adjust these times to suit your preferences and work style.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-1 text-tomato-700">What if I finish a task before the timer ends?</h3>
              <p>Use the remaining time to review or improve your work, or to plan your next task. The goal is to maintain focus for the full pomodoro period.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-1 text-tomato-700">What if I get interrupted during a pomodoro?</h3>
              <p>If the interruption is unavoidable, stop the timer, handle the interruption, and then start a new pomodoro. If it's something that can wait, make a note of it and continue with your current pomodoro.</p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-1 text-tomato-700">Is this technique good for all types of work?</h3>
              <p>The Pomodoro Technique works well for many types of tasks, especially those requiring focused attention. However, it might not be ideal for work that requires long periods of uninterrupted flow, such as complex problem-solving or creative work.</p>
            </div>
          </div>
          
          <div className="bg-tomato-100 p-6 rounded-xl border border-tomato-200 shadow-lg">
            <h2 className="text-2xl font-display font-bold text-tomato-700 mb-4">About this App</h2>
            <p className="text-tomato-800">
              Pomodoro is a digital implementation of the Pomodoro Technique, designed to help you improve your productivity and focus. With our app, you can:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-tomato-800">
              <li>Track your work sessions and breaks</li>
              <li>Manage your tasks and estimate required pomodoros</li>
              <li>Customize timer durations to suit your preferences</li>
              <li>Get visual and audio notifications</li>
              <li>Access your data across devices</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
